/**
 * Settings > Roles and Permissions — list roles, user count, section access (Phase 1).
 * PERM-005, SETT-004: list roles, show user count; system roles not deletable.
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";

export interface RoleRow {
  id: string;
  name: string;
  description?: string;
  is_system: boolean;
  user_count: number;
}

const SECTIONS = [
  { id: "status", label: "Status" },
  { id: "dashboards", label: "Dashboards" },
  { id: "call_history", label: "Call history" },
  { id: "settings", label: "Settings" },
];

export default function SettingsRoles() {
  const { getToken } = useAuth();
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [configuringRole, setConfiguringRole] = useState<RoleRow | null>(null);

  const fetchRoles = useCallback(async () => {
    const token = await getToken();
    if (!token) {
      setError("Not signed in");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const base = typeof window !== "undefined" ? window.location.origin : "";
    try {
      const res = await fetch(`${base}/api/roles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setRoles(data.roles ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load roles");
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  if (loading) return <p style={{ color: "var(--text-muted)" }}>Loading…</p>;
  if (error) return <p style={{ color: "var(--error)" }}>{error}</p>;

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Roles and Permissions</h2>
      <p style={{ color: "var(--text-muted)", marginBottom: "1rem" }}>
        Built-in roles define access to sections and data. User count shows how many users have each role. Section access can be refined per role (configure below).
      </p>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--header-border)" }}>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600 }}>Role</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600 }}>Description</th>
              <th style={{ textAlign: "right", padding: "0.75rem", fontWeight: 600 }}>Users</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600 }}>Type</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {roles.map((role) => (
              <tr key={role.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                <td style={{ padding: "0.75rem", fontWeight: 500 }}>{role.name}</td>
                <td style={{ padding: "0.75rem", color: "var(--text-muted)" }}>{role.description ?? "—"}</td>
                <td style={{ padding: "0.75rem", textAlign: "right" }}>{role.user_count}</td>
                <td style={{ padding: "0.75rem" }}>
                  {role.is_system ? (
                    <span style={{ fontSize: "0.75rem", padding: "0.2rem 0.5rem", background: "var(--widget-bg)", borderRadius: "4px" }}>System</span>
                  ) : (
                    <span style={{ color: "var(--text-muted)" }}>Custom</span>
                  )}
                </td>
                <td style={{ padding: "0.75rem" }}>
                  <button
                    type="button"
                    onClick={() => setConfiguringRole(role)}
                    style={{
                      padding: "0.35rem 0.75rem",
                      fontSize: "0.8125rem",
                      border: "1px solid var(--widget-bg)",
                      borderRadius: "6px",
                      background: "var(--bg)",
                      color: "var(--accent)",
                      cursor: "pointer",
                    }}
                  >
                    Configure
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {configuringRole && (
        <div
          style={{
            marginTop: "1.5rem",
            padding: "1rem",
            background: "var(--widget-bg)",
            borderRadius: "8px",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <h3 style={{ margin: 0, fontSize: "1rem" }}>Section access — {configuringRole.name}</h3>
            <button
              type="button"
              onClick={() => setConfiguringRole(null)}
              style={{
                padding: "0.35rem 0.75rem",
                border: "1px solid var(--widget-bg)",
                borderRadius: "6px",
                background: "transparent",
                color: "var(--text-muted)",
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
          <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", marginBottom: "1rem" }}>
            Which sections this role can access. Data scope (e.g. Agent = own, Supervisor = team) is defined by role and enforced in the app.
          </p>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                <th style={{ textAlign: "left", padding: "0.5rem 0.75rem", fontWeight: 600, fontSize: "0.875rem" }}>Section</th>
                <th style={{ textAlign: "center", padding: "0.5rem 0.75rem", fontWeight: 600, fontSize: "0.875rem" }}>View</th>
                <th style={{ textAlign: "center", padding: "0.5rem 0.75rem", fontWeight: 600, fontSize: "0.875rem" }}>Edit</th>
              </tr>
            </thead>
            <tbody>
              {SECTIONS.map((sec) => (
                <tr key={sec.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding: "0.5rem 0.75rem" }}>{sec.label}</td>
                  <td style={{ padding: "0.5rem 0.75rem", textAlign: "center" }}>
                    <span style={{ color: "var(--text-muted)" }}>✓</span>
                  </td>
                  <td style={{ padding: "0.5rem 0.75rem", textAlign: "center" }}>
                    <span style={{ color: "var(--text-muted)" }}>{configuringRole.name === "Super Admin" || configuringRole.name === "Admin" ? "✓" : "—"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ color: "var(--text-muted)", fontSize: "0.8125rem", marginTop: "0.75rem" }}>
            Full per-section and per-widget permissions will be configurable in a future update. Today, access is determined by role (Agent, Supervisor, Admin, Super Admin).
          </p>
        </div>
      )}

      <p style={{ color: "var(--text-muted)", fontSize: "0.8125rem", marginTop: "1rem" }}>
        To assign a role to a user, go to <strong>Users</strong> and edit the user. Custom roles (add/edit/delete) can be added in a future release.
      </p>
    </div>
  );
}
